<script setup lang="ts">
/**
 * Строка списка задач — макет 12:342: чекбокс, тип, ключ, название, статус,
 * приоритет, исполнитель и срок. Состояния строки — макет 70:139.
 *
 * Статус и исполнитель меняются прямо здесь: список — то место, где размечают
 * несколько задач подряд, и ради каждой открывать карточку значит потерять
 * очередь. Остальное правят в панели задачи, которую раскрывает клик по строке
 * (DEV-1474), или на её странице — по ключу.
 *
 * Колонки «Пайплайн» в строке нет: `ListIssues` о пайплайнах не знает, а
 * запрашивать их по задаче — это пятнадцать запросов на страницу. Приедет
 * вместе с пакетной ручкой (DEV-1475), место под неё в сетке уже есть.
 */
import { AppAvatar } from '@vinprop/design-system'
import { computed, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { isClosedCategory } from '@/api/statusCategory'
import InlinePicker from '@/components/InlinePicker.vue'
import type { ProjectColumn, ProjectMember } from '@/features/projects/api'

import { PRIORITIES } from '../api'
import { formatDueDate, formatStamp, toDateInput } from '../format'
import IssueTypeIcon from './IssueTypeIcon.vue'
import PriorityGlyph from './PriorityGlyph.vue'
import StatusBadge from './StatusBadge.vue'
import SubtaskCount from './SubtaskCount.vue'

import type { IssueRow } from '../api'
import type { V1Priority } from '@/api/clients/tracker-issue'

const props = defineProps<{
  issue: IssueRow
  columns: ProjectColumn[]
  members: ProjectMember[]
  isSelected: boolean
  /** Какой момент показывает последняя колонка: правку или закрытие (DEV-2041) */
  stampField?: 'updatedAt' | 'completedAt'
  /** Строка правлена и числится не в своей секции — переедет при обновлении */
  pendingRegroup?: boolean
}>()

const emit = defineEmits<{
  toggle: [id: string, shift: boolean]
  move: [id: string, columnId: string]
  assign: [id: string, assigneeId: string, assigneeName: string, assigneeAvatar: string]
  /** Инлайн-пикеры приоритета и срока (DEV-2053, desktop-only) */
  prioritize: [id: string, priority: V1Priority]
  due: [id: string, dueDate: string]
  /** Контекст-меню: «…» или правый клик; координаты — куда его открыть */
  menu: [issue: IssueRow, x: number, y: number]
}>()

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()

const column = computed(() => props.columns.find((entry) => entry.id === props.issue.columnId))

const due = computed(() => formatDueDate(props.issue.dueDate, locale.value))

/**
 * «Обновлена» — колонка дефолтной сортировки: без неё порядок «свежие сверху»
 * невидим и выглядит случайным (DEV-2041). На табе закрытых — дата закрытия.
 */
const stamp = computed(() =>
  formatStamp(props.issue[props.stampField ?? 'updatedAt'], locale.value, {
    today: t('issue.today'),
    yesterday: t('issue.yesterday'),
  }),
)

/**
 * Просроченный срок красный — но только у незакрытой задачи: у сделанной
 * прошлая дата это не долг, а история, и красить её значит звать чинить то,
 * что уже сделано.
 */
const isOverdue = computed(() => {
  if (!props.issue.dueDate) return false
  if (isClosedCategory(column.value?.statusCategory)) return false

  return new Date(props.issue.dueDate).getTime() < Date.now()
})

const statusOptions = computed(() =>
  props.columns.map((entry) => ({ value: entry.id, label: entry.name })),
)

const priorityOptions = computed(() =>
  PRIORITIES.map((value) => ({ value, label: t(`issues.priorities.${value}`) })),
)

/** Срок правится нативным date-инпутом по клику (DEV-2053): свой календарь
 *  ради одной ячейки — лишний компонент, а платформа умеет сама. */
const isDueEditing = ref(false)
const dueInput = ref<HTMLInputElement | null>(null)

const editDue = async () => {
  isDueEditing.value = true

  await nextTick()

  dueInput.value?.focus()
  dueInput.value?.showPicker?.()
}

const commitDue = () => {
  if (!isDueEditing.value) return

  isDueEditing.value = false

  const value = dueInput.value?.value ?? ''
  const next = value ? new Date(`${value}T00:00:00`).toISOString() : ''

  if (next !== (props.issue.dueDate || '')) emit('due', props.issue.id, next)
}

/** Снять исполнителя — такой же выбор, как назначить: пустое значение сверху */
const assigneeOptions = computed(() => [
  { value: '', label: t('issues.list.noAssignee') },
  ...props.members.filter((member) => member.name).map((m) => ({ value: m.id, label: m.name })),
])

const onAssign = (assigneeId: string) => {
  const member = props.members.find((entry) => entry.id === assigneeId)

  emit('assign', props.issue.id, assigneeId, member?.name ?? '', member?.avatar ?? '')
}

/**
 * Клик по строке раскрывает панель задачи — кроме кликов по тому, что здесь
 * работает само: чекбоксу, пикерам статуса и исполнителя и ключу, который
 * уводит на страницу целиком.
 */
const onOpen = (event: MouseEvent) => {
  if ((event.target as HTMLElement).closest('a, button, input')) return

  void router.push({ query: { ...route.query, issue: props.issue.key } })
}

/** «…» открывает меню под кнопкой; правый клик — у курсора (DEV-2051) */
const onMenuButton = (event: MouseEvent) => {
  const box = (event.currentTarget as HTMLElement).getBoundingClientRect()

  emit('menu', props.issue, box.left, box.bottom + 4)
}

const onContextMenu = (event: MouseEvent) => {
  emit('menu', props.issue, event.clientX, event.clientY)
}
</script>

<template>
  <div
    class="irow"
    :class="{ 'irow--selected': isSelected }"
    role="button"
    tabindex="0"
    :aria-label="t('issues.list.openIssue', { key: issue.key })"
    @click="onOpen"
    @keydown.enter="router.push({ query: { ...route.query, issue: issue.key } })"
    @contextmenu.prevent="onContextMenu"
  >
    <!-- Отметка по клику, а не по change: только у клика есть shiftKey,
         а Shift-диапазон — половина смысла отметок (DEV-2043). -->
    <input
      class="irow__check"
      type="checkbox"
      :checked="isSelected"
      :aria-label="t('issues.list.select', { key: issue.key })"
      @click.stop="emit('toggle', issue.id, ($event as MouseEvent).shiftKey)"
    />

    <IssueTypeIcon :type="issue.type" />

    <!-- Ключ — постоянный адрес задачи: по нему уходят на страницу целиком,
         а название раскрывает панель, не уводя из списка. -->
    <RouterLink class="irow__key" :to="`/issue/${issue.key}`">{{ issue.key }}</RouterLink>

    <!-- Счётчик подзадач живёт в ячейке названия: своей колонки под него в
         сетке нет, а рядом с названием он и читается — «что это за задача и
         сколько в ней осталось» — одним взглядом. -->
    <span class="irow__name">
      <span class="irow__title">{{ issue.title }}</span>
      <SubtaskCount :total="issue.subtasks.total" :done="issue.subtasks.done" />
      <!-- Правленая строка не переезжает в свою секцию до обновления — иначе
           список прыгал бы под рукой; помета честно говорит об этом. -->
      <span v-if="pendingRegroup" class="irow__regroup" :title="t('issues.list.willRegroup')">↷</span>
    </span>

    <InlinePicker
      :options="statusOptions"
      :model-value="issue.columnId"
      :empty-text="t('toolbar.filters.empty')"
      @update:model-value="emit('move', issue.id, $event)"
    >
      <template #trigger="{ toggle }">
        <button type="button" class="irow__inline" :title="t('issues.list.changeStatus')" @click="toggle()">
          <StatusBadge :name="column?.name ?? '—'" :color="column?.color ?? ''" />
        </button>
      </template>
    </InlinePicker>

    <InlinePicker
      :options="priorityOptions"
      :model-value="issue.priority"
      :empty-text="t('toolbar.filters.empty')"
      @update:model-value="emit('prioritize', issue.id, $event as V1Priority)"
    >
      <template #trigger="{ toggle }">
        <button
          type="button"
          class="irow__inline"
          :title="t('menu.priority')"
          @click="toggle()"
        >
          <PriorityGlyph :priority="issue.priority" />
        </button>
      </template>
    </InlinePicker>

    <InlinePicker
      align="right"
      :options="assigneeOptions"
      :model-value="issue.assigneeId"
      :empty-text="t('issues.create.noMembers')"
      @update:model-value="onAssign"
    >
      <template #trigger="{ toggle }">
        <button
          type="button"
          class="irow__inline"
          :title="t('issues.list.changeAssignee')"
          @click="toggle()"
        >
          <AppAvatar :name="issue.assigneeName" :src="issue.assigneeAvatar" :size="24" />
        </button>
      </template>
    </InlinePicker>

    <input
      v-if="isDueEditing"
      ref="dueInput"
      class="irow__due-input"
      type="date"
      :value="toDateInput(issue.dueDate)"
      @change="commitDue"
      @blur="commitDue"
      @keydown.escape="isDueEditing = false"
    />
    <button
      v-else
      type="button"
      class="irow__inline irow__due"
      :class="{ 'irow__due--overdue': isOverdue }"
      :title="t('menu.due')"
      @click="editDue"
    >
      {{ due || '—' }}
    </button>

    <span class="irow__stamp">{{ stamp || '—' }}</span>

    <!-- «…» появляется на hover: постоянная кнопка в каждой строке — шум.
         На тач-экранах hover нет — там меню открывает long-press (правый
         клик системы) по строке. -->
    <button
      type="button"
      class="irow__menu"
      :aria-label="t('menu.open', { key: issue.key })"
      @click.stop="onMenuButton"
    >
      …
    </button>
  </div>
</template>

<style scoped>
.irow {
  display: grid;
  grid-template-columns: var(--itable-columns);
  align-items: center;
  gap: 12px;
  /* Строка — ребёнок колоночного флекса, и без этого она сплющивается под
     доступную высоту: на длинной странице от сорока пикселей остаётся
     двадцать восемь, и таблица едет вся разом. */
  flex-shrink: 0;
  height: 40px;
  padding: 0 16px;
  border-top: 1px solid var(--color-border);
}

.irow:hover {
  background: var(--color-bg-muted);
}

/* Двойной класс не для красоты: у `:hover` специфичность выше, и отмеченная
   строка под курсором иначе выглядит как обычная — ровно в тот момент, когда
   на неё смотрят. */
.irow.irow--selected {
  background: var(--color-primary-tint);
}

.irow__check {
  width: 16px;
  height: 16px;
  /* Свой браузерный margin у флажка сдвигает всю колонку — см. шапку таблицы */
  margin: 0;
  accent-color: var(--color-primary);
  cursor: pointer;
}

.irow__key {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  text-decoration: none;
  /* Строка моноширинного текста выше своего кегля, и без явной высоты она
     растягивает ячейку — соседние колонки от этого разъезжаются по вертикали. */
  line-height: 16px;
}

.irow__name {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.irow__title {
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  line-height: 17px;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.irow__key:hover {
  color: var(--color-primary);
  text-decoration: underline;
}

.irow__title:hover {
  text-decoration: underline;
}

/* Триггеры инлайн-правки — не кнопки на вид: в макете это сама пилюля и сам
   аватар, кнопочная рамка вокруг них превратила бы строку в панель. */
.irow__inline {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  border-radius: var(--radius-full);
  background: none;
  cursor: pointer;
}

.irow__inline:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

.irow__due {
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  font-variant-numeric: tabular-nums;
  line-height: 16px;
}

.irow__due--overdue {
  color: var(--color-danger);
}

.irow__due-input {
  height: 24px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
}

.irow__stamp {
  overflow: hidden;
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  font-variant-numeric: tabular-nums;
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.irow__regroup {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
  cursor: help;
}

.irow__menu {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-muted);
  font-size: var(--font-size-body);
  line-height: 1;
  cursor: pointer;
  opacity: 0;
}

.irow:hover .irow__menu,
.irow__menu:focus-visible {
  opacity: 1;
}

.irow__menu:hover {
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
}
</style>
